import { ToasterService } from "@Front/Components/DesignSystem/Toaster";
import { EToastPriority, Toasts } from "@Front/Stores/Toasts";
import { NETWORK_ERROR_TITLE } from "./errorMessages/constants/otherErrorPatterns";

const ERROR_TOAST_DURATION_MS = 6000;
/** Fixed toast id so at most one network toast is ever visible (react-toastify dedupes on toastId). */
export const NETWORK_ERROR_TOAST_ID = "network-error-toast";
const DEDUPE_MAP_PRUNE_THRESHOLD = 20;

const lastShownAtByToastId = new Map<string, number>();

function buildErrorToastId(title: string, message: string): string {
	return title === NETWORK_ERROR_TITLE ? NETWORK_ERROR_TOAST_ID : `error-toast:${title}:${message}`;
}

function isDuplicateWhileVisible(toastId: string, now: number): boolean {
	const lastShownAt = lastShownAtByToastId.get(toastId);
	return lastShownAt !== undefined && now - lastShownAt < ERROR_TOAST_DURATION_MS;
}

function pruneExpiredEntries(now: number): void {
	if (lastShownAtByToastId.size < DEDUPE_MAP_PRUNE_THRESHOLD) return;
	for (const [toastId, shownAt] of lastShownAtByToastId) {
		if (now - shownAt >= ERROR_TOAST_DURATION_MS) {
			lastShownAtByToastId.delete(toastId);
		}
	}
}

/**
 * User-facing API errors: floating toaster + in-app notification list (Toasts store).
 * Single entry point for ApiErrorHandlerService and HTTP client onError helpers.
 * Identical toasts never stack while one is visible; network toasts share a single slot
 * (re-armed after closure — react-toastify toastId — or after any transport success).
 */
export function showErrorToasterAndNotificationCenter(title: string, message: string): void {
	const toastId = buildErrorToastId(title, message);
	const now = Date.now();
	if (isDuplicateWhileVisible(toastId, now)) {
		return;
	}
	pruneExpiredEntries(now);
	lastShownAtByToastId.set(toastId, now);
	ToasterService.getInstance().error({ title, description: message, toastId });
	try {
		Toasts.getInstance().open({
			title,
			text: message,
			priority: EToastPriority.HIGH,
			time: ERROR_TOAST_DURATION_MS,
		});
	} catch {
		// Toasts store may be unavailable during early bootstrap
	}
}

/** Dismisses and re-arms the network toast after a successful transport (called by the fetch helper). */
export function releaseNetworkErrorToast(): void {
	if (!lastShownAtByToastId.has(NETWORK_ERROR_TOAST_ID)) return;
	lastShownAtByToastId.delete(NETWORK_ERROR_TOAST_ID);
	ToasterService.getInstance().dismiss(NETWORK_ERROR_TOAST_ID);
}
